import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import api from "../services/api";
import { userAPI } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { normalizeFollowedBrandIdsList } from "../utils/userPreferences";
import { normalizeMealTimeForApi } from "../utils/mealTimeFilter";

export const FOLLOWED_BRAND_IDS_QUERY_KEY = ["followedBrandIds"];

const fetchFollowedBrandIds = async () => {
  const response = await userAPI.getFollowedBrands();
  const followedBrandIds = response.data?.followedBrandIds || [];
  return normalizeFollowedBrandIdsList(followedBrandIds);
};

/**
 * Same return shape as useDeals, but scoped to followed brands + optional filters.
 * Followed brand IDs are fetched once and cached (longer stale time); paginated
 * /deals requests reuse them instead of re-fetching IDs on every page.
 */
export const useFollowingDeals = (filters, _isMobile, options = {}) => {
  const {
    refetchOnMount = false,
    refetchOnWindowFocus = false,
    refetchOnReconnect = false,
    enabled = true,
    ...queryOptions
  } = options;
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  const area = filters?.area || "";
  const date = filters?.date || "";
  const timeOfDay = normalizeMealTimeForApi(filters?.timeOfDay);
  const category = filters?.category || "";
  const search = filters?.search || "";
  const priceBand = filters?.priceBand || "";
  const discountType = filters?.discountType || "";

  const combinedEnabled = enabled && isAuthenticated;

  const idsQuery = useQuery({
    queryKey: FOLLOWED_BRAND_IDS_QUERY_KEY,
    queryFn: fetchFollowedBrandIds,
    enabled: combinedEnabled,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const brandIdsMemo = useMemo(() => idsQuery.data ?? [], [idsQuery.data]);
  const brandIdsKey = brandIdsMemo.length
    ? [...brandIdsMemo].map(String).sort().join(",")
    : "";

  const dealsQueryKey = useMemo(
    () => [
      "followingDeals",
      brandIdsKey,
      {
        area,
        date,
        timeOfDay,
        category,
        search,
        priceBand,
        discountType,
        pageSize: 50,
      },
    ],
    [brandIdsKey, area, date, timeOfDay, category, search, priceBand, discountType],
  );

  const queryFn = useCallback(
    async ({ pageParam = 1 }) => {
      if (!brandIdsMemo.length) {
        return { data: [], pageInfo: {} };
      }

      const params = {
        brandIds: brandIdsMemo.join(","),
        activeOnly: true,
        pageSize: 50,
        ...(typeof pageParam === "number" && pageParam > 0
          ? { page: pageParam }
          : { page: 1 }),
        ...(area && area !== "" && { area }),
        ...(date && { date }),
        ...(timeOfDay && { time_of_day: timeOfDay }),
        ...(category && { category }),
        ...(search?.trim() && { search: search.trim() }),
        ...(priceBand && priceBand !== "any" && { price_band: priceBand }),
        ...(discountType && { discount_type: discountType }),
      };

      const allowedIds = new Set(brandIdsMemo.map((id) => String(id)));

      const res = await api.get("/deals", { params });
      const payload = res?.data;
      const raw = payload?.data || [];
      const pageInfoIn = payload?.pageInfo || {};
      const pageSize = pageInfoIn.pageSize ?? params.pageSize ?? 50;
      const serverHasMore = !!pageInfoIn.hasMore;
      const hasMore = serverHasMore && raw.length >= pageSize;

      const data = raw.filter((deal) => {
        const bid = deal?.brand?._id;
        return bid != null && allowedIds.has(String(bid));
      });

      return {
        data,
        pageInfo: { ...pageInfoIn, hasMore },
      };
    },
    [brandIdsMemo, area, date, timeOfDay, category, search, priceBand, discountType],
  );

  const idsReady = idsQuery.isSuccess || idsQuery.isError;

  const query = useInfiniteQuery({
    queryKey: dealsQueryKey,
    queryFn,
    enabled: combinedEnabled && idsReady,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.pageInfo?.hasMore ? allPages.length + 1 : undefined,
    initialPageParam: 1,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnMount,
    refetchOnWindowFocus,
    refetchOnReconnect,
    ...queryOptions,
  });

  const deals = useMemo(
    () => query.data?.pages.flatMap((page) => page.data) || [],
    [query.data],
  );

  const queryRef = useRef(query);
  queryRef.current = query;

  const fetchDeals = useCallback((cursor, append) => {
    if (append && cursor) {
      queryRef.current.fetchNextPage();
    } else {
      queryRef.current.refetch();
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    const handleFollowedBrandsChanged = () => {
      queryClient.invalidateQueries({ queryKey: FOLLOWED_BRAND_IDS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ["followingDeals"] });
    };

    window.addEventListener("followedBrandsChanged", handleFollowedBrandsChanged);

    return () => {
      window.removeEventListener(
        "followedBrandsChanged",
        handleFollowedBrandsChanged,
      );
    };
  }, [isAuthenticated, queryClient]);

  const loadingIds = combinedEnabled && idsQuery.isLoading && !idsQuery.data;
  const loadingDeals =
    combinedEnabled &&
    idsReady &&
    query.isLoading &&
    !query.data;

  return {
    deals,
    loading: loadingIds || loadingDeals,
    status: query.isError ? "error" : query.isSuccess ? "success" : "idle",
    loadingMore: query.isFetchingNextPage,
    hasMore: query.hasNextPage,
    nextCursor: query.hasNextPage ? (query.data?.pages?.length || 0) + 1 : null,
    fetchDeals,
    refetch: query.refetch,
  };
};
